import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { useAuth } from '../../contexts/AuthContext';
import { Mic, Volume2, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLANS = [
  {
    key: 'voice_standard',
    name: 'Parler',
    tier: 'Standard',
    price: '+19',
    desc: 'Voix IA naturelle pour consulter les dossiers et memoires de votre entreprise.',
    icon: Mic,
    color: 'text-blue-500',
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/30',
  },
  {
    key: 'voice_clone',
    name: 'Voix clonee',
    tier: 'Premium',
    price: '+39',
    desc: 'Votre clone repond avec la voix reelle du collaborateur (technologie ElevenLabs).',
    icon: Volume2,
    color: 'text-violet-500',
    bg: 'bg-violet-500/10',
    border: 'border-violet-500/30',
  },
];

export function VoiceUpsellDialog({ open, onOpenChange, requiredFeature }) {
  const { user, token, tenant, refreshUser } = useAuth();
  const [activating, setActivating] = useState(null);
  const [notifying, setNotifying] = useState(false);

  const isOwner = user?.role === 'owner';
  const features = tenant?.features || {};

  const handleActivate = async (featureKey) => {
    setActivating(featureKey);
    try {
      await axios.patch(`${API}/tenant/features`, {
        feature: featureKey,
        enabled: true,
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success(`${featureKey === 'voice_standard' ? 'Parler (Standard)' : 'Voix clonee (Premium)'} active !`);
      if (refreshUser) refreshUser();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'activation');
    } finally {
      setActivating(null);
    }
  };

  const handleNotifyOwner = async () => {
    setNotifying(true);
    try {
      await axios.post(`${API}/tenant/notify-owner`, {
        feature: requiredFeature || 'voice_standard',
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success('Notification envoyee a l\'administrateur');
      onOpenChange(false);
    } catch (e) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setNotifying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="voice-upsell-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-heading">
            {isOwner ? 'Activer la Voix IA pour votre equipe' : 'Cette fonctionnalite necessite l\'option Voix IA'}
          </DialogTitle>
          <DialogDescription className="text-sm">
            {isOwner
              ? 'Choisissez le niveau de voix IA a activer pour votre equipe.'
              : 'Demandez a votre administrateur d\'activer cette option.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          {PLANS.map((plan) => {
            const isActive = features[plan.key];
            const Icon = plan.icon;
            return (
              <Card key={plan.key} className={`${isActive ? 'border-emerald-500/40 bg-emerald-500/5' : plan.border} transition-colors`} data-testid={`upsell-plan-${plan.key}`}>
                <CardContent className="py-4 flex items-start gap-4">
                  <div className={`p-2.5 rounded-lg ${plan.bg} shrink-0`}>
                    <Icon className={`w-5 h-5 ${plan.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-sm">{plan.name}</span>
                      <Badge variant="outline" className={`text-[10px] ${plan.key === 'voice_clone' ? 'border-violet-300 text-violet-600' : 'border-blue-300 text-blue-600'}`}>
                        {plan.tier}
                      </Badge>
                      {isActive && (
                        <Badge className="bg-emerald-600/15 text-emerald-700 border-emerald-200 text-[10px] gap-1">
                          <CheckCircle className="w-3 h-3" /> Actif
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{plan.desc}</p>
                    <p className="text-sm font-semibold mt-2">{plan.price} EUR / utilisateur / mois</p>
                  </div>
                  {isOwner && !isActive && (
                    <Button
                      size="sm"
                      variant="default"
                      onClick={() => handleActivate(plan.key)}
                      disabled={!!activating}
                      className="shrink-0"
                      data-testid={`activate-${plan.key}`}
                    >
                      {activating === plan.key ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Activer'}
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {!isOwner && (
          <div className="mt-4 flex justify-end">
            <Button onClick={handleNotifyOwner} disabled={notifying} className="gap-2" data-testid="notify-owner-btn">
              {notifying ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Notifier l'owner
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
